import { create } from "zustand";

/**
 * National Sync Store (v2)
 * 
 * Manages envelope generation, signature hashing, ledger synchronization,
 * and retry policies.
 */
export interface NationalSyncStoreState {
  envelopeSizeKB: number;
  signatureHash: string;
  ledgerHeight: number;
  ackStatus: "pending" | "accepted" | "rejected";
  retries: number;

  // Actions
  setEnvelopeSizeKB: (size: number) => void;
  setSignatureHash: (hash: string) => void;
  setLedgerHeight: (height: number) => void;
  setAckStatus: (status: "pending" | "accepted" | "rejected") => void;
  setRetries: (retries: number) => void;

  // Actions
  generateSignedEnvelope: () => { envelopeSizeKB: number; signatureHash: string; ledgerHeight: number };
  sendToLedger: () => Promise<void>;

  // Derived
  getRetryPolicy: () => { nextRetryMs: number; maxRetries: number };

  // Export
  exportSyncEnvelope: () => void;
}

// Exponential backoff: 3 attempts
const RETRY_DELAYS = [1000, 2000, 4000]; // ms

export const useNationalSyncStore = create<NationalSyncStoreState>((set, get) => ({
  envelopeSizeKB: 0,
  signatureHash: "",
  ledgerHeight: 0,
  ackStatus: "pending",
  retries: 0,

  setEnvelopeSizeKB: (size) => set({ envelopeSizeKB: Math.max(0, size) }),
  setSignatureHash: (hash) => set({ signatureHash: hash }),
  setLedgerHeight: (height) => set({ ledgerHeight: Math.max(0, height) }),
  setAckStatus: (status) => set({ ackStatus: status }),
  setRetries: (retries) => set({ retries: Math.max(0, retries) }),

  generateSignedEnvelope: () => {
    const envelopeSizeKB = Math.floor(Math.random() * 500) + 100;
    const signatureHash = `0x${Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join("")}`;
    const ledgerHeight = Math.floor(Math.random() * 1000000) + 100000;

    set({ envelopeSizeKB, signatureHash, ledgerHeight });
    return { envelopeSizeKB, signatureHash, ledgerHeight };
  },

  sendToLedger: async () => {
    const state = get();
    set({ ackStatus: "pending" });

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Mock: 70% success rate
    const success = Math.random() > 0.3;
    if (success) {
      set({ ackStatus: "accepted", retries: 0 });
    } else {
      const newRetries = state.retries + 1;
      set({ retries: newRetries });
      if (newRetries >= 3) {
        set({ ackStatus: "rejected" });
      } else {
        set({ ackStatus: "pending" });
      }
    }
  },

  getRetryPolicy: () => {
    const retries = get().retries;
    return {
      nextRetryMs: retries < RETRY_DELAYS.length ? RETRY_DELAYS[retries] : RETRY_DELAYS[RETRY_DELAYS.length - 1],
      maxRetries: 3,
    };
  },

  exportSyncEnvelope: () => {
    const state = get();
    const report = {
      envelopeSizeKB: state.envelopeSizeKB,
      signatureHash: state.signatureHash,
      ledgerHeight: state.ledgerHeight,
      ackStatus: state.ackStatus,
      retries: state.retries,
      retryPolicy: state.getRetryPolicy(),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "syncEnvelope.json";
    a.click();
    URL.revokeObjectURL(url);
  },
}));











